import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {Observable, Subscription} from 'rxjs';
import {ConfirmationService} from 'primeng/api';
import {MvsMessageService} from 'features/core/shared/service/message/mvs-message.service';
import {MvsObjectNavigationService} from 'features/core/shared/navigation/mvs-object-navigation.service';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {QlQueryDto} from 'features/core/shared/ql/data/dto/ql-query.dto';
import {MvsWidgetComponent} from 'features/core/shared/widget/component/runtime/mvs-widget/mvs-widget.component';
import {CpRunReviewLookupService} from '../../../service/ql/cp-run-review-lookup.service';
import {CpRunTransactionKind} from '../../../interface/cp-run-transaction-kind.interface';
import {CpRunTransactionTotals} from '../../../interface/cp-run-transaction-totals.interface';
import {buildCpRunWidgetForm, CpRunWidgetColumn} from '../../../service/domain/cp-run-widget-column';
import {cpRowValue} from '../../../service/domain/cp-provision-assignment-columns';
import {cpOpenProvisionAgent} from '../../../service/domain/cp-object-navigation';
import {CpFeatureRight} from '../../../service/domain/cp-feature-right';
import {cpServerMessage} from '../../../service/domain/cp-server-message';
import {CpAgentStatementResult} from '../../../interface/cp-agent-statement-result.interface';
import {ProvisionRunStatus} from '../../../enum/provision-run-status.enum';
import {CpProvisionRunService} from '../../../service/api/cp-provision-run.service';
import {CpProvisionAgentStatementService} from '../../../service/api/cp-provision-agent-statement.service';
import {CpRunStatementLookupService} from '../../../service/ql/cp-run-statement-lookup.service';
import {CpRunStatementRef} from '../../../interface/cp-run-statement-ref.interface';
import {CpRunStatementMode} from '../../../interface/cp-run-statement-mode.interface';

/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: Shared
 * Reason: Summenzeile of the two transaction tabs of the run detail ("Vertrags-Transaktionen",
 *         "Konto-Transaktionen"): the total amount and number of bookings of the run, plus — on
 *         demand — the total per Berater as a framework widget table over a QL `groupBy`.
 *
 * The single headline figure (Summe + Anzahl) is not a table and comes from the lookup service. The
 * per-Berater breakdown IS a table and is therefore a `createWidgetTableQl` widget (fe_core_widget
 * TW-1) with a curated `WidgetForm`: it gets sorting, paging and export from the framework instead
 * of a hand-rolled `<p-table>`. Both use the SAME run filter as the worklist below, so the figures
 * always add up to what the table lists.
 *
 * Actions: the jump to an Abrechnungskonto (`#cust_openAccount` column) and — for the kind `agent`
 * only — the "Abrechnung" column (`#cust_agentStatement`, GR-S1 status matrix driven by
 * {@link runStatus}):
 *   - CALCULATED / IN_REVIEW / ACTIVATED: "Entwurf herunterladen" (watermarked draft PDF, never stored);
 *   - PAID_OUT / COMPLETED: "Abrechnung herunterladen" when the agent has an ACTIVE statement in this
 *     run, else "Abrechnung erstellen" (confirm, `createAgentStatement$`, then the lookup is reloaded);
 *   - every other status: no column.
 * Which agent HAS a statement comes from ONE QL query per run ({@link CpRunStatementLookupService}),
 * never one per row; the lookup is reloaded on its own, the widget is NOT rebuilt for it (the cells
 * read the map), so sorting and paging of the table survive a create.
 *
 * WIDGET LIFECYCLE (widget-created-once): the per-agent widget is BUILT only when the table is
 * expanded, the run or the kind changes, or the run status moves the column set between
 * none/draft/final. A {@link reloadToken} bump (job end) reloads the mounted widget IN PLACE
 * (`MvsWidgetComponent.refreshWidget()`), so the user's sort and paging survive every job end.
 *
 * RIGHT `cp_agentStatement`: module pattern of {@link CpFeatureRight} — optimistic (the button is
 * live), a server 403 demotes it stickily for this component instance and says why in German.
 */
@Component({
    selector: 'cp-run-detail-totals',
    templateUrl: './cp-run-detail-totals.component.html',
    styleUrls: ['./cp-run-detail-totals.component.scss'],
    standalone: false
})
export class CpRunDetailTotalsComponent implements OnInit, OnChanges, OnDestroy {

    /** fieldId of the action column; the layout field is `#cust_openAccount`. */
    readonly openAccountFieldId: string = 'openAccount';

    /** fieldId of the statement column (kind `agent` only); the layout field is `#cust_agentStatement`. */
    readonly agentStatementFieldId: string = 'agentStatement';

    /** The run whose bookings are summed. */
    @Input() provisionRunId: number;

    /** `contract` = cp.ProvisionContractTransaction, `agent` = cp.ProvisionAgentTransaction. */
    @Input() kind: CpRunTransactionKind = 'contract';

    /**
     * Bump to reload in place (the host increments it on every job end, the same moment it reloads
     * its worklists) — the totals must never lag behind the table.
     */
    @Input() reloadToken: number = 0;

    /**
     * Status of the run — decides the "Abrechnung" column of the kind `agent` (GR-S1). Without a
     * status (or for the kind `contract`) the column is not shown.
     */
    @Input() runStatus: ProvisionRunStatus;

    busy: boolean;
    initialized: boolean;
    loadFailed: boolean;

    totals: CpRunTransactionTotals;

    /** Per-agent table expanded? Collapsed by default so the worklist keeps its height. */
    perAgentVisible: boolean = false;

    /** Widget table of the per-agent totals; only built while {@link perAgentVisible}. */
    perAgentWidget: WidgetData;

    /** Mounted per-agent widget instance — only for the in-place reload on {@link reloadToken}. */
    @ViewChild('perAgentWidgetRef') perAgentWidgetComponent: MvsWidgetComponent;

    /**
     * ACTIVE statements of the run per provisionAgentId (final mode only). `null` = not (yet)
     * loaded; an agent absent from a loaded map has no final statement.
     */
    statementsByAgent: Map<number, CpRunStatementRef> = null;

    /** The statement lookup failed — the final cells offer a retry instead of guessing. */
    statementLookupFailed: boolean = false;

    /** Rows (provisionAgentId) with a statement download/create in flight. */
    busyStatementAgentIds: Set<number> = new Set<number>();

    /** Sticky server 403 on create — the module's optimistic-right pattern ({@link CpFeatureRight}). */
    agentStatementDeniedByServer: boolean = false;

    /** Mode the per-agent widget was built for; a status change rebuilds it only when this changes. */
    private builtStatementMode: CpRunStatementMode = 'none';

    private loadSubscription: Subscription;
    private statementLookupSubscription: Subscription;
    private statementActionSubscriptions: Subscription[] = [];

    constructor(protected lookupService: CpRunReviewLookupService,
                protected navigationService: MvsObjectNavigationService,
                protected provisionRunService: CpProvisionRunService,
                protected agentStatementService: CpProvisionAgentStatementService,
                protected statementLookupService: CpRunStatementLookupService,
                protected confirmationService: ConfirmationService,
                protected messageService: MvsMessageService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    /** Initialize component. */
    initComponent(): void {
        this.refreshComponent();
        this.initialized = true;
    }

    /** (Re)load the totals and — if expanded — rebuild the per-agent widget (run / kind changed). */
    refreshComponent(): void {
        this.refreshPerAgentWidget();
        this.refreshStatementLookup();
        this.loadTotals();
    }

    /**
     * Reload in place (job end, {@link reloadToken}): the headline totals, the statement lookup and
     * the MOUNTED per-agent widget — the widget instance is kept, so sort and paging survive.
     */
    reloadInPlace(): void {
        this.perAgentWidgetComponent?.refreshWidget();
        this.refreshStatementLookup();
        this.loadTotals();
    }

    /** Load the headline figure (Summe + Anzahl) of the run. */
    protected loadTotals(): void {

        this.loadSubscription?.unsubscribe();
        this.loadFailed = false;

        if (!this.provisionRunId) {
            this.totals = null;
            this.busy = false;
            return;
        }

        this.busy = true;

        this.loadSubscription = this.lookupService.loadTransactionTotals(this.provisionRunId, this.kind).subscribe({
            next: totals => {
                this.totals = totals;
                this.busy = false;
            },
            error: () => {
                this.totals = null;
                this.loadFailed = true;
                this.busy = false;
            }
        });
    }

    togglePerAgent(): void {
        this.perAgentVisible = !this.perAgentVisible;
        this.refreshPerAgentWidget();
        this.refreshStatementLookup();
    }

    /** Open the Abrechnungskonto of one per-agent row (QL key `pa.id`), read-only. */
    openAccount(row: any): void {
        cpOpenProvisionAgent(this.navigationService, this.toId(cpRowValue(row, 'pa.id')));
    }

    /** True when the per-agent row has an Abrechnungskonto to jump to. */
    hasAccount(row: any): boolean {
        return !!this.toId(cpRowValue(row, 'pa.id'));
    }

    // ── Abrechnung column (kind `agent`) ───────────────────────────────────────────────────────

    /** Statement action of the current run status (GR-S1); `none` for the kind `contract`. */
    get statementMode(): CpRunStatementMode {
        if (this.kind !== 'agent' || this.runStatus === null || this.runStatus === undefined) {
            return 'none';
        }
        switch (this.runStatus) {
            case ProvisionRunStatus.CALCULATED:
            case ProvisionRunStatus.IN_REVIEW:
            case ProvisionRunStatus.ACTIVATED:
                return 'draft';
            case ProvisionRunStatus.PAID_OUT:
            case ProvisionRunStatus.COMPLETED:
                return 'final';
            default:
                return 'none';
        }
    }

    /** provisionAgentId of a per-agent row (QL key `pa.id`), null when the row has no account. */
    agentIdOf(row: any): number | null {
        return this.toId(cpRowValue(row, 'pa.id'));
    }

    /** The ACTIVE statement of the row's agent, null when none (or not loaded). */
    statementOf(row: any): CpRunStatementRef | null {
        const agentId = this.agentIdOf(row);
        return agentId && this.statementsByAgent ? (this.statementsByAgent.get(agentId) ?? null) : null;
    }

    /** True while a statement action of this row is running. */
    isStatementBusy(row: any): boolean {
        const agentId = this.agentIdOf(row);
        return !!agentId && this.busyStatementAgentIds.has(agentId);
    }

    /** Effective grant of `cp_agentStatement` — optimistic until the server answered 403. */
    get canCreateAgentStatement(): boolean {
        return CpFeatureRight.resolve(this.agentStatementDeniedByServer, null, null);
    }

    /** Download the watermarked draft of one agent (CALCULATED / IN_REVIEW / ACTIVATED). */
    downloadDraft(row: any): void {
        const agentId = this.agentIdOf(row);
        if (!agentId || this.isStatementBusy(row)) {
            return;
        }
        this.runStatementAction(agentId,
            this.provisionRunService.downloadAgentStatementPdf$(this.provisionRunId, agentId),
            'Download fehlgeschlagen',
            'Der Entwurf der Provisionsabrechnung konnte nicht geladen werden.');
    }

    /** Download the stored ACTIVE statement of one agent (PAID_OUT / COMPLETED). */
    downloadFinal(row: any): void {
        const agentId = this.agentIdOf(row);
        const statement = this.statementOf(row);
        if (!agentId || !statement?.statementId || this.isStatementBusy(row)) {
            return;
        }
        this.runStatementAction(agentId,
            this.agentStatementService.downloadDocument$(statement.statementId),
            'Download fehlgeschlagen',
            'Die Provisionsabrechnung ' + (statement.statementNumber ?? '') + ' konnte nicht geladen werden.');
    }

    /**
     * Create the FINAL statement of one agent after a confirmation. The server is idempotent (an
     * existing ACTIVE statement is returned), so a double click or a parallel job cannot produce a
     * second one; afterwards the one-per-run lookup is reloaded and the cell turns into the download.
     */
    createFinal(row: any): void {

        const agentId = this.agentIdOf(row);
        if (!agentId || this.isStatementBusy(row)) {
            return;
        }

        if (!this.canCreateAgentStatement) {
            this.messageService.showErrorMessage(CpFeatureRight.DENIED_HEADER,
                CpFeatureRight.deniedMessage(CpFeatureRight.AGENT_STATEMENT));
            return;
        }

        const agentName: string = cpRowValue(row, 'ag.name') ?? '';

        this.confirmationService.confirm({
            header: 'Abrechnung erstellen',
            message: 'Die finale Provisionsabrechnung' + (agentName ? ' für ' + agentName : '')
                + ' wird erstellt und abgelegt. Sie erhält eine Abrechnungsnummer und kann danach nur noch '
                + 'mit Begründung neu ausgestellt werden.',
            icon: 'fa-regular fa-file-invoice',
            acceptLabel: 'Erstellen',
            rejectLabel: 'Abbrechen',
            accept: () => this.executeCreateFinal(agentId)
        });
    }

    /** Reload the one-per-run statement lookup (retry of a failed load). */
    reloadStatements(): void {
        this.refreshStatementLookup();
    }

    protected executeCreateFinal(agentId: number): void {

        this.busyStatementAgentIds.add(agentId);

        const subscription = this.provisionRunService.createAgentStatement$(this.provisionRunId, agentId).subscribe({
            next: (outcome: CpAgentStatementResult) => {
                this.busyStatementAgentIds.delete(agentId);
                const number: string = outcome?.statementNumber ? outcome.statementNumber + ' ' : '';
                if (outcome?.result === 'SKIPPED_EXISTING') {
                    // idempotent repeat: an ACTIVE statement existed already and is returned unchanged
                    this.messageService.showInfoMessage('Abrechnung bereits vorhanden',
                        outcome.message || ('Die Provisionsabrechnung ' + number + 'existierte bereits.'));
                } else {
                    this.messageService.showSuccessMessage('Abrechnung erstellt',
                        outcome?.message || ('Die Provisionsabrechnung ' + number + 'wurde erstellt.'));
                }
                // CREATED and SKIPPED_EXISTING alike: the cell must turn into the download
                this.refreshStatementLookup();
            },
            error: error => {
                this.busyStatementAgentIds.delete(agentId);
                if (CpFeatureRight.isForbidden(error)) {
                    this.agentStatementDeniedByServer = true;
                    this.messageService.showErrorMessage(CpFeatureRight.DENIED_HEADER,
                        CpFeatureRight.deniedMessage(CpFeatureRight.AGENT_STATEMENT));
                    return;
                }
                this.showServerError(error, 'Abrechnung nicht erstellt',
                    'Die Provisionsabrechnung konnte nicht erstellt werden. Bitte prüfen Sie die Meldungen des Laufs.');
                // the server may have refused because a statement exists meanwhile — re-read the truth
                this.refreshStatementLookup();
            }
        });
        this.trackStatementAction(subscription);
    }

    /** Keep the action subscription for ngOnDestroy; finished ones are dropped on the way. */
    private trackStatementAction(subscription: Subscription): void {
        this.statementActionSubscriptions = this.statementActionSubscriptions.filter(existing => !existing.closed);
        this.statementActionSubscriptions.push(subscription);
    }

    /** Shared busy/error frame of the two downloads (the blob is saved by the service). */
    protected runStatementAction(agentId: number, action$: Observable<Blob>,
                                 errorTitle: string, fallback: string): void {

        this.busyStatementAgentIds.add(agentId);

        const subscription = action$.subscribe({
            next: () => this.busyStatementAgentIds.delete(agentId),
            error: error => {
                this.busyStatementAgentIds.delete(agentId);
                this.showServerError(error, errorTitle, fallback);
            },
            complete: () => this.busyStatementAgentIds.delete(agentId)
        });
        this.trackStatementAction(subscription);
    }

    /**
     * Load the ACTIVE statements of the run — ONE QL query per run, only while the per-agent table
     * is expanded in final mode. A failure is kept apart from "none" so no cell offers "erstellen"
     * for an agent whose statement merely could not be read.
     */
    protected refreshStatementLookup(): void {

        this.statementLookupSubscription?.unsubscribe();
        this.statementLookupFailed = false;

        if (!this.perAgentVisible || !this.provisionRunId || this.statementMode !== 'final') {
            this.statementsByAgent = null;
            return;
        }

        this.statementsByAgent = null;
        const runId = this.provisionRunId;

        this.statementLookupSubscription = this.statementLookupService.getActiveStatementsByAgent(runId).subscribe({
            next: byAgent => {
                if (runId === this.provisionRunId) {
                    this.statementsByAgent = byAgent ?? new Map<number, CpRunStatementRef>();
                }
            },
            error: () => {
                if (runId === this.provisionRunId) {
                    this.statementsByAgent = null;
                    this.statementLookupFailed = true;
                }
            }
        });
    }

    /**
     * German error toast: the server's own sentence when it sent one (`CoreApiResponseDto.data`,
     * also inside a Blob body of a file request), the caller's wording otherwise.
     */
    protected showServerError(error: any, title: string, fallback: string): void {
        cpServerMessage(error)
            .then(message => this.messageService.showErrorMessage(title, message || fallback))
            .catch(() => this.messageService.showErrorMessage(title, fallback));
    }

    /** Build the per-agent widget while expanded, drop it while collapsed (always a fresh instance). */
    protected refreshPerAgentWidget(): void {

        this.perAgentWidget = null;
        this.builtStatementMode = this.statementMode;

        if (!this.perAgentVisible || !this.provisionRunId) {
            return;
        }

        const columns: CpRunWidgetColumn[] = [
            {field: 'ag.name', label: 'Berater'},
            {field: 'ag.id', label: 'Vermittler-Id', outputControl: 'text'},
            {field: 'e.cnt', label: 'Buchungen'},
            {field: 'e.total', label: 'Summe', outputControl: 'currencyEuro'},
            {field: '#cust_' + this.openAccountFieldId, label: 'Abrechnungskonto'}
        ];
        if (this.builtStatementMode !== 'none') {
            columns.push({field: '#cust_' + this.agentStatementFieldId, label: 'Abrechnung'});
        }

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: Per-Berater totals of the bookings of one provision run — QL groupBy on
         *         cp.ProvisionContractTransaction or cp.ProvisionAgentTransaction (filter
         *         provisionRunId) by agent: Berater, Vermittler-Id, number of bookings and sum,
         *         plus a #cust_ column that opens the agent's Abrechnungskonto read-only and — kind
         *         agent, statement-eligible run status — a #cust_ "Abrechnung" column (draft download,
         *         final download or create).
         */
        this.perAgentWidget = WidgetFactory.createWidgetTableQl(
            'cp.run.detail.transaction.perAgent.' + this.kind,
            'Summe je Berater',
            this.buildPerAgentQuery(),
            WidgetDataParam.create('size', 'S'),
            WidgetDataParam.create('widgetForm', buildCpRunWidgetForm(columns))
        );
    }

    /**
     * The per-agent `groupBy` — the same shape the lookup service sums with, so the widget rows add
     * up to the Summenzeile. For contract bookings the agent is reached over the reconcile anchor
     * `provisionContractAgent.provisionAgent.agent`, for account bookings over `provisionAgent.agent`.
     */
    protected buildPerAgentQuery(): QlQueryDto {
        return {
            name: 'cp.run.detail.transaction.perAgent.' + this.kind,
            type: 'view',
            start: this.kind === 'contract'
                ? {
                    type: 'groupBy',
                    name: 'cp.ProvisionContractTransaction',
                    as: 'e',
                    fields: [
                        {
                            name: '*', as: 'cnt', function: 'count',
                            label: 'Buchungen', dataType: 'Long', fieldType: 'function'
                        },
                        {
                            name: 'amount', as: 'total', function: 'sum',
                            label: 'Summe', dataType: 'BigDecimal', fieldType: 'function'
                        }
                    ],
                    joins: [
                        {
                            type: 'groupBy', name: 'provisionContractAgent', as: 'pca',
                            joins: [
                                {
                                    type: 'groupBy', name: 'provisionAgent', as: 'pa', fields: [{name: 'id'}],
                                    joins: [
                                        {
                                            type: 'groupBy', name: 'agent', as: 'ag',
                                            fields: [{name: 'id'}, {name: 'name'}]
                                        }
                                    ]
                                }
                            ]
                        }
                    ]
                }
                : {
                    type: 'groupBy',
                    name: 'cp.ProvisionAgentTransaction',
                    as: 'e',
                    fields: [
                        {
                            name: '*', as: 'cnt', function: 'count',
                            label: 'Buchungen', dataType: 'Long', fieldType: 'function'
                        },
                        {
                            name: 'amount', as: 'total', function: 'sum',
                            label: 'Summe', dataType: 'BigDecimal', fieldType: 'function'
                        }
                    ],
                    joins: [
                        {
                            type: 'groupBy', name: 'provisionAgent', as: 'pa', fields: [{name: 'id'}],
                            joins: [
                                {
                                    type: 'groupBy', name: 'agent', as: 'ag',
                                    fields: [{name: 'id'}, {name: 'name'}]
                                }
                            ]
                        }
                    ]
                },
            fixedFilters: [
                FilterCriteria.create('e.provisionRunId', FilterCriteria.cOperatorEqual, this.provisionRunId)
            ]
        };
    }

    private toId(value: any): number | null {
        const parsed = Number(value);
        return value !== null && value !== undefined && value !== '' && Number.isFinite(parsed) && parsed > 0
            ? parsed
            : null;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['provisionRunId'] || changes['kind']) {
            // another run or another booking kind = another query (and alias) — build anew
            this.refreshComponent();
            return;
        }
        // the column set changes only between none/draft/final — rebuild the widget only then
        const columnSetChanged: boolean = !!changes['runStatus'] && this.statementMode !== this.builtStatementMode;
        if (columnSetChanged) {
            this.refreshPerAgentWidget();
        }
        if (changes['reloadToken']) {
            if (columnSetChanged) {
                // the widget was just rebuilt and loads fresh data itself — only totals + lookup
                this.refreshStatementLookup();
                this.loadTotals();
            } else {
                this.reloadInPlace();
            }
        } else if (changes['runStatus']) {
            this.refreshStatementLookup();
        }
    }

    ngOnDestroy(): void {
        this.loadSubscription?.unsubscribe();
        this.statementLookupSubscription?.unsubscribe();
        this.statementActionSubscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
